import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  Alert,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Block as BlockIcon
} from '@mui/icons-material';
import api from '../services/api';

const AdBlockingPage = () => {
  const [domains, setDomains] = useState([]);
  const [settings, setSettings] = useState({ enabled: true });
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingDomain, setEditingDomain] = useState(null);
  const [formData, setFormData] = useState({
    domain: '',
    description: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadDomains();
    loadSettings();
  }, []);

  const loadDomains = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/ad-block-domains');
      setDomains(response.data);
    } catch (err) {
      setError('Ошибка загрузки доменов');
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await api.get('/admin/ad-block-settings');
      setSettings(response.data);
    } catch (err) {
      setError('Ошибка загрузки настроек');
    }
  };

  const handleOpenDialog = (domain = null) => {
    if (domain) {
      setEditingDomain(domain);
      setFormData({
        domain: domain.domain,
        description: domain.description || ''
      });
    } else {
      setEditingDomain(null);
      setFormData({
        domain: '',
        description: ''
      });
    }
    setOpenDialog(true);
    setError('');
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingDomain(null);
    setFormData({
      domain: '',
      description: ''
    });
    setError('');
  };

  const handleToggleGlobalBlocking = async () => {
    setLoading(true);
    try {
      const response = await api.put('/admin/ad-block-settings', {
        enabled: !settings.enabled
      });
      setSettings(response.data);
      setSuccess(`Блокировка ${response.data.enabled ? 'включена' : 'выключена'}`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Ошибка изменения настроек');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.domain.trim()) {
      setError('Введите домен');
      return;
    }

    setLoading(true);
    try {
      if (editingDomain) {
        await api.put(`/admin/ad-block-domains/${editingDomain.id}`, formData);
        setSuccess('Домен успешно обновлён');
      } else {
        await api.post('/admin/ad-block-domains', formData);
        setSuccess('Домен успешно добавлен');
      }
      handleCloseDialog();
      loadDomains();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот домен?')) {
      return;
    }

    setLoading(true);
    try {
      await api.delete(`/admin/ad-block-domains/${id}`);
      setSuccess('Домен успешно удалён');
      loadDomains();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Ошибка удаления домена');
    } finally {
      setLoading(false);
    }
  };


  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            <BlockIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Блокировка рекламы
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Список рекламных сетей для блокировки через WebView плеер.
            Домены будут блокироваться на стороне клиентского приложения.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Добавить домен
        </Button>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 3, p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6">
              Глобальная блокировка рекламы
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Всего доменов в списке: {domains.length}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              label={settings.enabled ? 'Включена' : 'Выключена'}
              color={settings.enabled ? 'success' : 'default'}
              icon={<BlockIcon />}
            />
            <Switch
              checked={settings.enabled}
              onChange={handleToggleGlobalBlocking}
              color="primary"
              disabled={loading}
            />
          </Box>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Домен</TableCell>
              <TableCell>Описание</TableCell>
              <TableCell align="center">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {domains.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    Нет доменов для блокировки. Добавьте первый домен.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              domains.map((domain) => (
                <TableRow key={domain.id}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {domain.domain}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {domain.description || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(domain)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(domain.id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingDomain ? 'Редактировать домен' : 'Добавить домен'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Домен"
              placeholder="yandex.ru/ads/adfox"
              value={formData.domain}
              onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
              sx={{ mb: 2 }}
              helperText="Введите домен или путь для блокировки (например: yandex.ru/ads/adfox)"
            />
            <TextField
              fullWidth
              label="Описание"
              placeholder="Яндекс Реклама"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              sx={{ mb: 2 }}
              helperText="Опциональное описание рекламной сети"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Отмена</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>
            {editingDomain ? 'Сохранить' : 'Добавить'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdBlockingPage;
